import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

/** The rule types offered for each concept, in the words an admin would use. */
const RULE_LABEL = {
  lifecycle_stage: { customer: 'A company enters a lifecycle stage', other: 'A contact enters a lifecycle stage' },
  contact_field: 'A contact field matches',
  deal_created: 'A deal is created',
  deal_stage: 'A deal enters a stage',
  deal_field: 'A deal field matches',
  company_field: 'A company field matches',
  first_won_deal: 'A company wins its first deal',
};

const OBJECT_OF_RULE = { contact_field: 'contacts', deal_field: 'deals', company_field: 'companies' };
const OPERATORS = [
  { value: 'is_set', label: 'has any value' },
  { value: 'equals', label: 'equals' },
  { value: 'at_least', label: 'is at least' },
];

const labelOfRule = (type, concept) => {
  const label = RULE_LABEL[type];
  return typeof label === 'string' ? label : label[concept === 'customer' ? 'customer' : 'other'];
};

/** A new rule of a type, with sensible starting values. */
export function startingRule(type, concept) {
  switch (type) {
    case 'lifecycle_stage':
      return { type, stage: { mql: 'marketingqualifiedlead', sql: 'salesqualifiedlead', customer: 'customer' }[concept] };
    case 'deal_created':
    case 'first_won_deal':
      return { type, dealTypes: [] };
    case 'deal_stage':
      return { type, stages: [], dealTypes: [] };
    default:
      return { type, property: '', operator: 'is_set', dateProperty: 'createdate' };
  }
}

function ruleIsComplete(rule) {
  if (rule.type === 'deal_stage') return rule.stages?.length > 0;
  if (OBJECT_OF_RULE[rule.type]) {
    return Boolean(rule.property) && (rule.operator === 'is_set' || String(rule.value ?? '').trim() !== '');
  }
  return true;
}

function Checks({ legend, items, value = [], onChange }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <fieldset className="connection-field">
      <legend>{legend}</legend>
      <div className="connection-checks">
        {items.map((i) => (
          <label key={i.value}>
            <input type="checkbox" checked={value.includes(i.value)} onChange={() => toggle(i.value)} aria-label={`${legend}: ${i.label}`} />
            {i.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function FieldRule({ rule, fields, label, onChange }) {
  const property = fields.find((f) => f.name === rule.property);
  const dateFields = fields.filter((f) => f.type === 'date');
  const set = (patch) => onChange({ ...rule, ...patch });
  return (
    <div className="definition-field-rule">
      <label className="connection-field">
        <span>Field</span>
        <select aria-label={`${label} field`} value={rule.property || ''} onChange={(e) => set({ property: e.target.value, value: '' })}>
          <option value="">Choose a field</option>
          {fields.map((f) => <option key={f.name} value={f.name}>{f.label}</option>)}
        </select>
      </label>
      <label className="connection-field">
        <span>Condition</span>
        <select aria-label={`${label} condition`} value={rule.operator} onChange={(e) => set({ operator: e.target.value })}>
          {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      {rule.operator !== 'is_set' && (
        <label className="connection-field">
          <span>Value</span>
          {property?.options?.length > 0 && rule.operator === 'equals'
            ? (
              <select aria-label={`${label} value`} value={rule.value || ''} onChange={(e) => set({ value: e.target.value })}>
                <option value="">Choose a value</option>
                {property.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )
            : <input type="text" aria-label={`${label} value`} value={rule.value || ''} onChange={(e) => set({ value: e.target.value })} />}
        </label>
      )}
      <label className="connection-field">
        <span>Dated by</span>
        <select aria-label={`${label} date`} value={rule.dateProperty || 'createdate'} onChange={(e) => set({ dateProperty: e.target.value })}>
          {dateFields.map((f) => <option key={f.name} value={f.name}>{f.label}</option>)}
          {!dateFields.some((f) => f.name === 'createdate') && <option value="createdate">Create date</option>}
        </select>
      </label>
    </div>
  );
}

/** One concept's definition, with what it matches shown as it changes. */
export default function DefinitionEditor({ connectorId, companyId, concept, label, hint, rule, options, onChange }) {
  const [preview, setPreview] = useState({ loading: false, text: '' });
  const allowed = options.conceptRules?.[concept] || [];
  const stageGroups = options.pipelines || [];

  // Show what the definition matches a moment after it stops changing.
  const ruleJson = JSON.stringify(rule);
  useEffect(() => {
    if (!ruleIsComplete(rule)) {
      setPreview({ loading: false, text: '' });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreview({ loading: true, text: '' });
      try {
        const res = await connectionsAPI.previewDefinition(connectorId, companyId, concept, rule);
        if (cancelled) return;
        const { count, noun, months } = res.data || {};
        setPreview({
          loading: false,
          text: count === null || count === undefined
            ? 'The number of matches is not available for this definition.'
            : `Matches ${count.toLocaleString()} ${noun} in the last ${months} months.`,
        });
      } catch (err) {
        if (!cancelled) setPreview({ loading: false, text: err?.response?.data?.error || 'The number of matches could not be read.' });
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // The rule is compared by its content, so a new object with the same rule does not ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorId, companyId, concept, ruleJson]);

  const set = (patch) => onChange({ ...rule, ...patch });
  const lifecycleOptions = concept === 'customer' ? (options.companyLifecycleStages || options.lifecycleStages) : options.lifecycleStages;

  return (
    <div className="definition" role="group" aria-label={`${label} definition`}>
      <h5>{label}</h5>
      <p className="source-hint">{hint}</p>
      <label className="connection-field">
        <span>Counts when</span>
        <select aria-label={`${label} counts when`} value={rule.type} onChange={(e) => onChange(startingRule(e.target.value, concept))}>
          {allowed.map((t) => <option key={t} value={t}>{labelOfRule(t, concept)}</option>)}
        </select>
      </label>

      {rule.type === 'lifecycle_stage' && (
        <label className="connection-field">
          <span>Lifecycle stage</span>
          <select aria-label={`${label} lifecycle stage`} value={rule.stage} onChange={(e) => set({ stage: e.target.value })}>
            {(lifecycleOptions || []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      )}

      {rule.type === 'deal_stage' && stageGroups.map((p) => (
        <Checks
          key={p.id}
          legend={`${label} stages in ${p.label}`}
          items={p.stages.map((s) => ({ value: s.id, label: s.label }))}
          value={rule.stages || []}
          onChange={(picked) => set({
            stages: [...(rule.stages || []).filter((id) => !p.stages.some((s) => s.id === id)), ...picked],
          })}
        />
      ))}

      {['deal_created', 'deal_stage', 'first_won_deal'].includes(rule.type) && (options.dealTypes || []).length > 0 && (
        <Checks
          legend={`${label} deal types`}
          items={options.dealTypes}
          value={rule.dealTypes || []}
          onChange={(dealTypes) => set({ dealTypes })}
        />
      )}
      {['deal_created', 'deal_stage', 'first_won_deal'].includes(rule.type) && (
        <p className="source-hint">Leave deal types unticked to include every type.</p>
      )}

      {OBJECT_OF_RULE[rule.type] && (
        <FieldRule rule={rule} fields={options.fields?.[OBJECT_OF_RULE[rule.type]] || []} label={label} onChange={onChange} />
      )}

      <p className="definition-preview" aria-live="polite">
        {preview.loading ? 'Checking HubSpot…' : preview.text}
      </p>
    </div>
  );
}
